// ISSUE-1041. What the Settings Contest rules card offers (an edit action per
// contest that has not ended, or a note saying why there is none) and what the
// saved message says (SUR-R73: a change counts from the next round that has
// not opened; the open round keeps its rules). The round number in that
// message is the server's effectiveFromRoundNumber from /contest/update.
//
// The status filter is a render choice keyed on the server's own contest
// status. CONTEST_ALREADY_ENDED on /contest/update remains the enforcement.
import { VIEWER_CONTEST_STATUS_CALLED_OFF } from "@sportsmet/contest-engine";
import type { useContestList } from "@sportsmet/contest-sdk/private";

export const CONTEST_RULES_EDIT_LABEL = "Change contest rules";
export const CONTEST_RULES_NO_CONTEST_NOTE =
  "This pool has no contest yet, so there are no contest rules to change.";
export const CONTEST_RULES_ENDED_NOTE =
  "This pool's contest has ended, so its rules can't change anymore.";
export const CONTEST_RULES_LIST_FAILED_MESSAGE =
  "We could not load this pool's contests.";
export const CONTEST_RULES_RETRY_LABEL = "Try again";
export const CONTEST_RULES_DIALOG_TITLE = "Change contest rules";
export const CONTEST_RULES_DIALOG_FAILED_MESSAGE =
  "We could not load these rules.";
export const CONTEST_RULES_DIALOG_ENDED_MESSAGE =
  "This contest has ended, so its rules can't change anymore.";
export const CONTEST_RULES_DATES_LOCKED_NOTE =
  "This contest has started, so its dates can't change.";
export const CONTEST_RULES_SAVE_LABEL = "Save rules";
export const CONTEST_RULES_SAVING_LABEL = "Saving your rules";
export const CONTEST_RULES_CANCEL_LABEL = "Cancel";
export const CONTEST_RULES_DISCARD_TITLE = "Discard your changes?";
export const CONTEST_RULES_DISCARD_BODY =
  "Your rule changes haven't been saved.";
export const CONTEST_RULES_DISCARD_CONFIRM_LABEL = "Discard";
export const CONTEST_RULES_DISCARD_CANCEL_LABEL = "Keep editing";
export const CONTEST_RULES_OPEN_ROUND_NOTE =
  "The round people are picking in now keeps the rules it opened with, and rounds already scored stay as they are.";
export const CONTEST_RULES_SAVED_NOT_STARTED =
  "Saved. The contest hasn't started yet, so these rules apply from its first round.";
export const CONTEST_RULES_SAVED_NEXT_ROUND =
  "Saved. The new rules start in the next round that opens. " +
  CONTEST_RULES_OPEN_ROUND_NOTE;

export const EDIT_CONTEST_RULES_DIALOG_NAME = "edit-contest-rules";

/** The server's contest status, read from the generated SDK so no product
 *  file spells the called off token itself (checkAmericanEnglish). */
export type typeContestListStatus = NonNullable<
  NonNullable<ReturnType<typeof useContestList>["data"]>["data"]
>["items"][number]["status"];

export const EDITABLE_CONTEST_STATUSES: readonly typeContestListStatus[] = [
  "upcoming",
  "active",
  "pendingOutcome",
];

/** True for a contest that has ended: confirmed, or called off. */
export function isContestStatusEnded(status: typeContestListStatus): boolean {
  return status === "confirmed" || status === VIEWER_CONTEST_STATUS_CALLED_OFF;
}

export type typeContestRulesEditTarget = { contestId: string; label: string };

export type typeContestRulesEditState =
  | { phase: "loading" }
  | { phase: "failed" }
  | { phase: "noContest" }
  | { phase: "ended" }
  | { phase: "ready"; targets: readonly typeContestRulesEditTarget[] };

export function resolveContestRulesEditState(input: {
  isLoading: boolean;
  isTransportError: boolean;
  errorCode: string | null;
  items:
    | readonly {
        contestId: string;
        name: string;
        status: typeContestListStatus;
      }[]
    | null;
}): typeContestRulesEditState {
  if (input.isTransportError || input.errorCode !== null) {
    return { phase: "failed" };
  }
  if (input.isLoading) return { phase: "loading" };
  if (input.items === null || input.items.length === 0) {
    return { phase: "noContest" };
  }
  const editable = input.items.filter((item) =>
    EDITABLE_CONTEST_STATUSES.includes(item.status),
  );
  if (editable.length === 0) return { phase: "ended" };
  return {
    phase: "ready",
    targets: editable.map((item) => ({
      contestId: item.contestId,
      label:
        editable.length === 1
          ? CONTEST_RULES_EDIT_LABEL
          : `Change rules for ${item.name}`,
    })),
  };
}

export function contestRulesSavedMessage(input: {
  effectiveFromRoundNumber: number | null;
  hasRounds: boolean;
}): string {
  const n = input.effectiveFromRoundNumber;
  const named = n !== null && Number.isInteger(n) && n >= 1;
  if (named && n === 1) return CONTEST_RULES_SAVED_NOT_STARTED;
  if (named) {
    return `Saved. The new rules start when round ${String(n)} opens. ${CONTEST_RULES_OPEN_ROUND_NOTE}`;
  }
  return input.hasRounds
    ? CONTEST_RULES_SAVED_NEXT_ROUND
    : CONTEST_RULES_SAVED_NOT_STARTED;
}
